// src/components/Atelier5/GuideModal.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { X, HelpCircle, Target, Shield, CheckCircle, FileText, Lightbulb } from 'lucide-react';

const GuideModal = ({ isOpen, onClose, className = "" }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const steps = [
    {
      title: t('workshop5.guide.steps.synthesis.title', 'Synthèse des scénarios'),
      description: t('workshop5.guide.steps.synthesis.description', 'Visualisez tous les scénarios stratégiques et leur évaluation des risques. Consultez les mesures de sécurité associées à chaque scénario.'),
      icon: <Target size={20} className="text-blue-500" />
    },
    {
      title: t('workshop5.guide.steps.measures.title', 'Gestion des mesures'),
      description: t('workshop5.guide.steps.measures.description', 'Gérez les mesures de sécurité techniques, organisationnelles et humaines. Affectez des responsables et des échéances.'),
      icon: <Shield size={20} className="text-purple-500" />
    },
    {
      title: t('workshop5.guide.steps.checklist.title', 'Fiche de suivi'),
      description: t('workshop5.guide.steps.checklist.description', 'Utilisez la checklist de mise en œuvre pour suivre l\'avancement. Générez des questions de contrôle et validez les versions.'),
      icon: <CheckCircle size={20} className="text-orange-500" />
    },
    {
      title: t('workshop5.guide.steps.reports.title', 'Rapports'),
      description: t('workshop5.guide.steps.reports.description', 'Générez des rapports complets de l\'atelier 5 avec la synthèse des risques, le plan de traitement et la fiche de suivi.'),
      icon: <FileText size={20} className="text-green-500" />
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className={`bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-6 ${className}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-6 flex justify-between items-center">
        <div className="flex items-center">
          <HelpCircle size={24} className="text-white mr-3" />
          <h2 className="text-xl font-bold text-white">{t('workshop5.guide.title', 'Guide - Atelier 5')}</h2>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
          aria-label={t('common.close', 'Close')}
        >
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex"
            >
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 font-bold">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {step.icon}
                  <h3 className="text-lg font-semibold text-gray-800 ml-2">{step.title}</h3>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </motion.div>
          ))}

          <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-md mt-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Lightbulb className="h-5 w-5 text-indigo-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-indigo-800">{t('workshop5.guide.advice.title', 'Conseil')}</h3>
                <div className="mt-2 text-sm text-indigo-700">
                  <p>
                    {t('workshop5.guide.advice.description', 'Commencez par examiner la synthèse des scénarios stratégiques, puis gérez les mesures de sécurité et utilisez la fiche de suivi pour valider la mise en œuvre.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GuideModal;
